import { getSupabase as getSupabaseNullable } from "../../lib/supabase";
import type { IOperationResult, IBatchResult, IGetAllOptions } from "../types";

function requireSupabase() {
  const client = getSupabaseNullable();
  if (!client) throw new Error("Supabase not initialized. Call initSupabase() first.");
  return client;
}

// ─────────────────────────────────────────────────────────────────────────────
// camelCase ↔ snake_case mapping
// ─────────────────────────────────────────────────────────────────────────────

function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

function mapKeys(
  obj: Record<string, unknown>,
  transform: (key: string) => string
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[transform(key)] = value;
  }
  return result;
}

/** Convert a camelCase TS object to snake_case for DB insert/update. */
export function toDb(obj: Record<string, unknown>): Record<string, unknown> {
  return mapKeys(obj, toSnakeCase);
}

/** Convert a snake_case DB row to camelCase for TS consumption. */
export function fromDb<T>(obj: Record<string, unknown>): T {
  return mapKeys(obj, toCamelCase) as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// Friendly error messages
// ─────────────────────────────────────────────────────────────────────────────

const CONSTRAINT_LABELS: Record<string, string> = {
  idx_locations_tenant_code: "code",
  idx_assets_tenant_code: "code",
  idx_parts_tenant_sku: "SKU",
  idx_vendors_tenant_name: "name",
  idx_teams_tenant_name: "name",
};

function humanizeDbError(message: string): string {
  // "duplicate key value violates unique constraint "idx_locations_tenant_code""
  const match = message.match(/unique constraint "(\w+)"/);
  if (match) {
    const field = CONSTRAINT_LABELS[match[1]] ?? match[1];
    return `A record with the same ${field} already exists.`;
  }
  // "null value in column "name" violates not-null constraint"
  if (message.includes("violates not-null constraint")) {
    const col = message.match(/column "(\w+)"/)?.[1];
    return col ? `${toCamelCase(col)} is required.` : "A required field is missing.";
  }
  return message;
}

// ─────────────────────────────────────────────────────────────────────────────
// Base service factory
// ─────────────────────────────────────────────────────────────────────────────

export interface BaseServiceOptions {
  /** The actual Postgres table name (for writes). */
  tableName: string;
  /** Optional view name for enriched reads (joins, computed columns). Falls back to tableName. */
  viewName?: string;
  /** Column names (snake_case) to search with ilike when `options.search` is provided. */
  searchColumns?: string[];
  /** camelCase field names that exist in the view but not the table. Stripped before insert/update. */
  computedColumns?: string[];
  /** Skip setting created_by/updated_by audit fields (for tables like audit_log, notifications). */
  skipAuditFields?: boolean;
}

/**
 * Creates a standard CRUD service backed by Supabase.
 * All services return `IOperationResult<T>` — never throws.
 *
 * Usage:
 * ```ts
 * const base = createBaseService<Location, LocationBase>({
 *   tableName: "locations",
 *   viewName: "locations_enriched",
 *   searchColumns: ["name", "code"],
 * });
 * export class LocationService {
 *   static getAll = base.getAll;
 *   static get = base.get;
 *   static create = base.create;
 *   static update = base.update;
 *   static delete = base.delete;
 * }
 * ```
 */
export function createBaseService<
  T extends { id: string },
  TBase extends Record<string, unknown>,
>(options: BaseServiceOptions) {
  const { tableName, viewName, searchColumns = [], computedColumns = [], skipAuditFields = false } = options;
  const readTarget = viewName ?? tableName;

  /** Remove computed/view-only fields before writing to the actual table. */
  function stripComputed(obj: Record<string, unknown>): Record<string, unknown> {
    if (computedColumns.length === 0) return obj;
    const clean = { ...obj };
    for (const col of computedColumns) {
      delete clean[col];
    }
    return clean;
  }

  return {
    async getAll(
      queryOptions?: IGetAllOptions
    ): Promise<IOperationResult<T[]>> {
      try {
        const supabase = requireSupabase();
        let query = supabase
          .from(readTarget)
          .select("*", { count: "exact" });

        // Filter: parse "camelField eq 'value'" → .eq(snake_field, value)
        if (queryOptions?.filter) {
          const clauses = queryOptions.filter.split(" and ");
          for (const clause of clauses) {
            const match = clause.trim().match(/^(\w+)\s+eq\s+'([^']*)'$/);
            if (match) {
              const [, field, value] = match;
              query = query.eq(toSnakeCase(field), value);
            }
          }
        }

        // Search: ilike on multiple columns
        if (queryOptions?.search && searchColumns.length > 0) {
          const term = `%${queryOptions.search}%`;
          const orClause = searchColumns
            .map((col) => `${col}.ilike.${term}`)
            .join(",");
          query = query.or(orClause);
        }

        // Sorting: translate "name asc" → .order("name", { ascending: true })
        if (queryOptions?.orderBy?.length) {
          for (const orderSpec of queryOptions.orderBy) {
            const [field, dir = "asc"] = orderSpec.split(" ");
            query = query.order(toSnakeCase(field), {
              ascending: dir === "asc",
            });
          }
        }

        // Pagination: offset + limit
        if (queryOptions?.skip != null && queryOptions?.top != null) {
          query = query.range(
            queryOptions.skip,
            queryOptions.skip + queryOptions.top - 1
          );
        } else if (queryOptions?.top != null) {
          query = query.limit(queryOptions.top);
        }

        const { data, error, count } = await query;
        if (error) return { success: false, error };

        const mapped = (data ?? []).map((row) =>
          fromDb<T>(row as Record<string, unknown>)
        );
        return {
          success: true,
          data: mapped,
          totalCount: count ?? undefined,
        };
      } catch (error) {
        return { success: false, error };
      }
    },

    async get(id: string): Promise<IOperationResult<T>> {
      try {
        const supabase = requireSupabase();
        const { data, error } = await supabase
          .from(readTarget)
          .select("*")
          .eq("id", id)
          .single();

        if (error) return { success: false, error };
        return {
          success: true,
          data: fromDb<T>(data as Record<string, unknown>),
        };
      } catch (error) {
        return { success: false, error };
      }
    },

    async create(record: TBase): Promise<IOperationResult<T>> {
      try {
        const supabase = requireSupabase();
        const dbRecord = toDb(stripComputed(record as Record<string, unknown>));

        // Set audit fields from current session
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          if (!skipAuditFields) {
            dbRecord.created_by = user.id;
            dbRecord.updated_by = user.id;
          }

          // Get tenant_id from JWT claims
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (session) {
            const payload = JSON.parse(
              atob(session.access_token.split(".")[1])
            );
            dbRecord.tenant_id = payload.tenant_id;
          }
        }

        const { data, error } = await supabase
          .from(tableName)
          .insert(dbRecord)
          .select()
          .single();

        if (error) return { success: false, error };
        return {
          success: true,
          data: fromDb<T>(data as Record<string, unknown>),
        };
      } catch (error) {
        return { success: false, error };
      }
    },

    async update(
      id: string,
      fields: Partial<TBase>
    ): Promise<IOperationResult<T>> {
      try {
        const supabase = requireSupabase();
        const dbFields = toDb(stripComputed(fields as Record<string, unknown>));

        // Set updated_by from current session
        if (!skipAuditFields) {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          if (user) {
            dbFields.updated_by = user.id;
          }
        }

        // Step 1: Write to the base table (no .select() — RLS may block reads here)
        const { error: updateError } = await supabase
          .from(tableName)
          .update(dbFields)
          .eq("id", id);

        if (updateError) return { success: false, error: updateError };

        // Step 2: Re-fetch from the enriched view (respects RLS read policies)
        const { data, error: readError } = await supabase
          .from(readTarget)
          .select("*")
          .eq("id", id)
          .single();

        if (readError) return { success: false, error: readError };
        return {
          success: true,
          data: fromDb<T>(data as Record<string, unknown>),
        };
      } catch (error) {
        return { success: false, error };
      }
    },

    async delete(id: string): Promise<IOperationResult<void>> {
      try {
        const supabase = requireSupabase();
        const { error } = await supabase.from(tableName).delete().eq("id", id);
        if (error) return { success: false, error };
        return { success: true };
      } catch (error) {
        return { success: false, error };
      }
    },

    async createMany(records: TBase[]): Promise<IBatchResult<T>> {
      const CHUNK_SIZE = 100;
      const allData: T[] = [];
      const allErrors: { index: number; error: string }[] = [];

      try {
        const supabase = requireSupabase();

        // Get user and tenant_id — same pattern as create()
        const {
          data: { user },
        } = await supabase.auth.getUser();

        let tenantId: string | undefined;
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          const payload = JSON.parse(
            atob(session.access_token.split(".")[1])
          );
          tenantId = payload.tenant_id;
        }

        // Prepare all rows: strip computed, inject audit fields, convert to snake_case
        const dbRows = records.map((record) => {
          const dbRecord = toDb(stripComputed(record as Record<string, unknown>));
          if (user && !skipAuditFields) {
            dbRecord.created_by = user.id;
            dbRecord.updated_by = user.id;
          }
          if (tenantId) {
            dbRecord.tenant_id = tenantId;
          }
          return dbRecord;
        });

        // Insert in chunks of CHUNK_SIZE
        for (let i = 0; i < dbRows.length; i += CHUNK_SIZE) {
          const chunk = dbRows.slice(i, i + CHUNK_SIZE);
          const chunkStartIndex = i;

          const { data, error } = await supabase
            .from(tableName)
            .insert(chunk)
            .select("*");

          if (!error && data) {
            // Chunk succeeded — map all rows
            for (const row of data) {
              allData.push(fromDb<T>(row as Record<string, unknown>));
            }
          } else {
            // Chunk failed — fallback to row-by-row to identify individual failures
            for (let j = 0; j < chunk.length; j++) {
              const rowResult = await supabase
                .from(tableName)
                .insert(chunk[j])
                .select("*")
                .single();

              if (rowResult.error) {
                allErrors.push({
                  index: chunkStartIndex + j,
                  error: humanizeDbError(rowResult.error.message),
                });
              } else if (rowResult.data) {
                allData.push(
                  fromDb<T>(rowResult.data as Record<string, unknown>)
                );
              }
            }
          }
        }

        return {
          success: allErrors.length === 0,
          data: allData,
          errors: allErrors,
        };
      } catch (error) {
        return {
          success: false,
          data: allData,
          errors: [
            ...allErrors,
            {
              index: -1,
              error:
                error instanceof Error ? error.message : "Unknown error",
            },
          ],
        };
      }
    },
  };
}
